import type { Translations } from '../types';

export const en: Translations = {
  // Navigation
  nav: {
    home: 'Home',
    meditations: 'Meditations',
    journal: 'Journal',
    tasks: 'Tasks',
    balance: 'Balance',
    profile: 'Profile',
  },

  // Common
  common: {
    loading: 'Loading...',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    back: 'Back',
    continue: 'Continue',
    skip: 'Skip',
    search: 'Search',
    all: 'All',
    today: 'Today',
    yesterday: 'Yesterday',
    daysAgo: 'd. ago',
    weeksAgo: 'w. ago',
    minutes: 'min',
    days: 'days',
    friend: 'friend',
  },

  // Home page
  home: {
    goodMorning: 'Good morning',
    goodAfternoon: 'Good afternoon',
    goodEvening: 'Good evening',
    howAreYou: 'How are you today?',
    trackMood: 'Track your emotional state and progress',
    shareMood: 'Share your mood',
    loadingMeditations: 'Loading meditations...',
    popularToday: 'Popular today',
    popularNow: 'Popular now',
    forSleep: 'For sleep',
    practiceCalendar: 'Practice calendar',
    dailyWisdom: 'Daily wisdom',
    wisdomQuote: '"Happiness is not in always doing what you want, but in always wanting what you do."',
  },

  // Profile page
  profile: {
    memberSince: 'With us since',
    consecutive: 'Consecutive',
    balance: 'Balance',
    myFriends: 'My friends',
    friends: 'Friends',
    earned: 'Earned',
    inviteFriend: 'Invite a friend',
    getCrystalsPerFriend: 'Get {amount} 💎 for each friend',
    copyLink: 'Copy link',
    copied: 'Copied!',
    copyAndSend: 'Copy and send the link to a friend',
    invitedFriends: 'Invited friends',
    joined: 'Joined',
    noFriendsYet: 'No one has joined via your link yet',
    settings: 'Settings',
    myPreferences: 'My preferences',
    notifications: 'Notifications',
    support: 'Support',
    aboutApp: 'About app',
    language: 'Language',
  },

  // Onboarding
  onboarding: {
    welcome: 'Hello',
    welcomeTitle: 'Hello.\nI am SanaSivana, the path to inner harmony',
    welcomeSubtitle: 'If things are difficult for you — you are in the right place. Here you can find peace through meditation, sort out your thoughts and find harmony.',
    experienceTitle: 'Your meditation experience',
    experienceSubtitle: 'Tell us about your experience so we can select the right practices',
    beginner: 'Beginner',
    beginnerDesc: 'Just starting to learn about meditation',
    intermediate: 'Some experience',
    intermediateDesc: 'Practiced a few times',
    advanced: 'Practice regularly',
    advancedDesc: 'Meditate constantly',
    goalsTitle: 'Your goals',
    goalsSubtitle: 'What do you want to achieve with meditation? You can select multiple options.',
    reduceStress: 'Reduce stress',
    improveSleep: 'Improve sleep',
    increaseFocus: 'Increase focus',
    manageEmotions: 'Manage emotions',
    findPeace: 'Find inner peace',
    preferencesTitle: 'Your preferences',
    preferencesSubtitle: 'What types of meditation are you interested in and how much time are you willing to dedicate to practice?',
    meditationTypes: 'Meditation types',
    mindfulness: 'Mindfulness',
    breathingPractices: 'Breathing practices',
    visualization: 'Visualization',
    mantras: 'Mantras',
    bodyScan: 'Body scan',
    sessionDuration: 'Session duration',
    congratulations: 'Congratulations!',
    rewardReceived: 'You received a reward for completing onboarding',
    continueEarning: 'Continue completing tasks and earn more 💎 to unlock meditations',
    startJourney: 'Start the journey',
  },

  // Balance page
  balancePage: {
    title: 'Balance',
    yourBalance: 'Your balance:',
    transactionHistory: 'Transaction history',
    income: 'Income',
    expense: 'Expense',
    noTransactions: 'No transactions to display',
  },

  // Tasks page
  tasksPage: {
    title: 'Tasks',
    subtitle: 'Complete tasks and earn crystals automatically',
    achievements: 'Achievements',
    newbie: 'Newbie',
    week: 'Week',
    month: 'Month',
    expert: 'Expert',
    completed: 'Completed',
    earned: 'Earned',
    allTasks: 'All tasks',
    loadingTasks: 'Loading tasks...',
  },

  // Friends page
  friendsPage: {
    title: 'Friends',
    searchPlaceholder: 'Search by name or username...',
    searchResults: 'Search results',
    searching: 'Searching...',
    noResults: 'No one found',
    friendRequests: 'Friend requests',
    myFriends: 'My friends',
    noFriendsYet: 'You have no friends yet',
    findFriends: 'Find friends using the search above',
    alreadyFriends: 'Friends',
    requestSent: 'Request sent',
    wantsToAdd: 'Wants to add you',
    add: 'Add',
    sending: 'Sending...',
    noPractices: 'No practices',
    lastPractice: 'Last practice:',
    medit: 'medit.',
    tasksShort: 'tasks',
    user: 'User',
  },

  // Meditation
  meditation: {
    catalog: 'Meditation catalog',
    catalogSubtitle: 'Video meditations for improving your inner state',
    loadingMeditations: 'Loading meditations...',
    category: 'Category',
    difficulty: 'Difficulty',
    allLevels: 'All levels',
    breathing: 'Breathing',
    meditationType: 'Meditation',
    relaxation: 'Relaxation',
    anxiety: 'Anxiety',
    sleep: 'Sleep',
    beginner: 'Beginner',
    intermediate: 'Intermediate',
    advanced: 'Advanced',
    noMeditations: 'No meditations found',
    tryFilters: 'Try changing the filters',
    startMeditation: 'Start meditation',
    startFor: 'Start meditation for',
    yourBalance: 'Your balance:',
    paid: 'Paid',
    free: 'Free',
    inProgress: 'In progress',
    completed: 'Completed',
    requiresCrystals: 'Requires 💎 {cost} to watch',
    description: 'Description',
    instructions: 'Instructions',
    author: 'Author',
    meditationCompleted: 'Meditation completed!',
    greatJob: 'Great job! Keep practicing regularly.',
    notFound: 'Meditation not found',
    backToCatalog: 'Back to catalog',
    loadingMeditation: 'Loading meditation...',
    costAlert: 'This meditation costs 💎 {cost}. Click "Start meditation" button to pay and watch.',
    audioMeditation: 'Audio meditation',
  },

  // Journal
  journal: {
    title: 'Mood journal',
    subtitle: 'Track your emotional state',
    addEntry: 'Add entry',
    noEntries: 'No entries yet',
    startJournal: 'Start journaling to better understand your emotions',
    loadingEntries: 'Loading entries...',
    newEntry: 'New entry',
    howDoYouFeel: 'How do you feel?',
    great: 'Great',
    good: 'Good',
    okay: 'Okay',
    bad: 'Bad',
    terrible: 'Terrible',
    note: 'Note',
    notePlaceholder: 'Describe what happened today, how you feel...',
    activitiesOptional: 'Activities (optional)',
    meditationActivity: 'Meditation',
    work: 'Work',
    reading: 'Reading',
    yoga: 'Yoga',
    social: 'Social',
    walk: 'Walk',
    selectMood: 'Please select a mood',
    addNote: 'Please add a note',
    savedSuccessfully: 'Entry saved successfully!',
    saveFailed: 'Failed to save entry. Please try again.',
    unsavedWarning: 'Are you sure? Unsaved data will be lost.',
  },

  // Preferences
  preferences: {
    title: 'My preferences',
    meditationExperience: 'Meditation experience',
    experienceDescription: 'Tell us about your meditation experience',
    meditationGoals: 'Meditation goals',
    goalsDescription: 'What do you want to achieve? (can select multiple)',
    meditationTypes: 'Meditation types',
    typesDescription: 'What practices are you interested in? (can select multiple)',
    sessionDuration: 'Session duration',
    durationDescription: 'How much time are you willing to dedicate to meditation?',
    saveChanges: 'Save changes',
  },

  // Notifications
  notifications: {
    title: 'Notifications',
    reminders: 'Reminders',
    updates: 'Updates',
    settingsHint: 'Settings apply to notifications we send to Telegram.',
    loadingSettings: 'Loading settings...',
  },

  // Support
  supportPage: {
    title: 'Support',
    newTicket: 'New ticket',
    subject: 'Subject',
    subjectPlaceholder: 'Briefly describe the issue',
    enterSubject: 'Enter the subject',
    subjectTooLong: 'Subject is too long',
    problemDescription: 'Problem description',
    descriptionPlaceholder: 'Describe your issue or question in detail...',
    enterMessage: 'Enter the message',
    messageTooLong: 'Message is too long',
    submitTicket: 'Submit ticket',
    submitting: 'Submitting...',
    createError: 'Error creating ticket. Please try again later.',
    openTickets: 'Open tickets',
    ticketOpen: 'Open',
    supportTeam: 'Support: ',
    you: 'You: ',
    ticketsHistory: 'Tickets history',
    allTickets: 'All tickets, including closed',
  },

  // About
  about: {
    title: 'About app',
    version: 'Version',
    aboutSanaSivana: 'About SanaSivana',
    aboutDescription: 'SanaSivana is your personal assistant in maintaining mental health. We offer meditation practices, stress reduction exercises, mood journaling and much more.',
    ourMission: 'Our mission',
    missionDescription: 'Make mental health support accessible and simple for everyone. We believe that taking care of psychological well-being should be part of everyone\'s daily routine.',
    information: 'Information',
    developer: 'Developer',
    releaseDate: 'Release date',
    category: 'Category',
    healthFitness: 'Health & Fitness',
    legalInfo: 'Legal information',
    termsOfService: 'Terms of Service',
    privacyPolicy: 'Privacy Policy',
    licenseAgreement: 'License Agreement',
    copyright: '© 2025 SanaSivana. All rights reserved.',
  },

  // Language
  languagePage: {
    title: 'Language',
    selectLanguage: 'Select language',
    english: 'English',
    russian: 'Русский',
    spanish: 'Español',
  },

  // Calendar
  calendar: {
    months: [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ],
    weekdays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    practiceDay: 'Practice day',
  },

  // Insufficient balance modal
  insufficientBalance: {
    title: 'Insufficient crystals',
    required: 'Required:',
    youHave: 'You have:',
    missing: 'Missing:',
    howToEarn: 'How to earn crystals?',
    completeTasks: 'Complete tasks',
    taskReward: 'Get up to 30 crystals per task',
    inviteFriends: 'Invite friends',
    friendReward: '15 crystals for each friend',
    practiceMeditations: 'Practice meditations',
    practiceReward: 'Regular practice brings rewards',
    topUpBalance: '💎 Top up balance',
    goToTasks: 'Go to tasks',
    inviteFriendsBtn: 'Invite friends',
  },

  // App loading
  appLoading: {
    loading: 'Loading SanaSivana...',
    connectingTelegram: 'Connecting to Telegram...',
    verifyingAuth: 'Verifying authentication...',
    checkingProgress: 'Checking your progress...',
  },
};

